import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { CreateFab } from '../../components/CreateFab';
import { PageHeader } from '../../components/PageHeader';
import { PersonRow } from '../../components/PersonRow';
import { PostCard } from '../../components/PostCard';
import { PulseRail } from '../../components/PulseRail';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ChallengeCard } from '../../components/engagement/ChallengeCard';
import { findCommunity } from '../../data/communities';
import { communityChallenges } from '../../data/engagement';
import { people } from '../../data/people';
import { useLocale } from '../../contexts/LocaleContext';
import { useSocial } from '../../contexts/SocialContext';
import { useScreenInit } from '../../useScreenInit.js';
import type { ChallengeDuration } from '../../types/engagement';
type Tab = 'posts' | 'pulse' | 'challenges' | 'members';
const challengeDurations: ChallengeDuration[] = ['weekly', 'monthly', 'annual'];

/** MOB/Community/Detail — an interest space: feed, Pulse and members. */
export function CommunityDetail() {
  const {
    communityId = ''
  } = useParams();
  const {
    t,
    tl,
    locale
  } = useLocale();
  const social = useSocial();
  const screenInit = useScreenInit() as {
    detailTab?: Tab;
  };
  const [tab, setTab] = useState<Tab>(screenInit.detailTab ?? 'posts');
  const community = findCommunity(communityId);
  if (!community) {
    return <div className="px-2 pb-2" data-id="element-2367" data-mp="38f02qjhkncw2">
        <PageHeader title={t('community.title')} showBack data-id="element-2368" data-mp="38frx5qzfn4kr" />
        <EmptyState title={t('community.missing')} body={t('community.none.body')} data-id="element-2369" data-mp="3dd9wqqrk87g3" />
      </div>;
  }
  const inCommunity = (category: string) => community.categories.some(item => item === category);
  const posts = social.posts.filter(post => inCommunity(post.context.category));
  const pulses = social.pulses.filter(ring => ring.items.some(item => inCommunity(item.kind)));
  const members = people.filter(person => person.id !== 'me' && inCommunity(person.category));
  const member = social.isMember(community.id);
  const challenges = communityChallenges.filter(item => item.communityId === community.id);
  return <div className="pb-2" data-id="element-2370" data-mp="v9laoatv4odb">
      <PageHeader title={tl(community.name)} showBack data-id="element-2371" data-mp="va3cdmzanusm" />

      <div className="px-2" data-id="element-2372" data-mp="1su7m9zji35tx">
        <div className="relative overflow-hidden rounded-panel" data-id="element-2373" data-mp="1sxlq7c7tbn3h">
          <img src={community.cover} alt="" aria-hidden="true" className="aspect-[16/7] w-full object-cover" data-id="element-2374" data-mp="1nxqu8u96k7bk" />
          <span className="absolute inset-0 bg-deep/55" aria-hidden="true" data-id="element-2375" data-mp="1e74orsv6gx6l" />
          <div className="absolute inset-0 flex flex-col justify-end p-2" data-id="element-2376" data-mp="2bm0brjzd28ww">
            <p className="bee-num text-[11px] font-medium text-ondark" data-id="element-2377" data-mp="2bo3d305yrogx">
              {t('community.members', {
              n: social.memberCount(community.id).toLocaleString(locale)
            })}{' '}
              · {t('community.weekly', {
              n: community.postsThisWeek
            })}
            </p>
            <p className="mt-[2px] max-w-[36ch] text-[13px] leading-snug text-sand" data-id="element-2378" data-mp="2j7m1xn1w2bxf">
              {tl(community.description)}
            </p>
          </div>
        </div>

        <Button variant={member ? 'outline' : 'primary'} size="lg" fullWidth className="mt-2" onClick={() => social.toggleMembership(community.id)} aria-pressed={member} data-id="element-2379" data-mp="2t4h0g5y5wcbp">
          {member && <CheckIcon size={16} className="text-gold-deep" data-id="element-2380" data-mp="36v3w1p8mlm3k" />}
          {member ? t('community.joined') : t('community.join')}
        </Button>

        <div className="mt-3" data-id="element-2381" data-mp="3bsvp4ml0be9c">
          <SegmentedTabs label={tl(community.name)} value={tab} onChange={setTab} tabs={[{
          id: 'posts',
          label: t('community.detail.posts')
        }, {
          id: 'pulse',
          label: t('community.detail.pulse')
        }, {
          id: 'challenges',
          label: t('eng.ch.tab')
        }, {
          id: 'members',
          label: t('community.detail.members')
        }]} data-id="element-2382" data-mp="3bw9rhuto7ngs" />
        </div>

        <div className="mt-3" data-id="element-2383" data-mp="v77b0grix7t3">
          {tab === 'posts' && (posts.length === 0 ? <EmptyState title={t('community.noPosts.title')} body={t('community.noPosts.body')} data-id="element-2384" data-mp="r4u2jkle6vsm" /> : <ul className="space-y-3" data-id="element-2385" data-mp="3hcgm5ot6rxk">
                {posts.map(post => <li key={post.id} data-id="element-2386" data-mp="3unvr1jqqgfvc">
                    <PostCard post={post} data-id="element-2387" data-mp="cfxuvon8zksj" />
                  </li>)}
              </ul>)}

          {tab === 'pulse' && (pulses.length === 0 ? <EmptyState title={t('community.noPulse.title')} body={t('community.noPulse.body')} data-id="element-2388" data-mp="1nvcsmuna6gop" /> : <PulseRail pulses={pulses} data-id="element-2389" data-mp="1iw07cec25ho8" />)}

          {tab === 'challenges' && (challenges.length === 0 ? <EmptyState title={t('eng.ch.none')} body={t('eng.ch.noneBody')} data-id="element-2390" data-mp="1vevrmacx6kix" /> : <div data-id="element-2391" data-mp="1hf2yk8dx7s7s">
                <p className="mb-2 text-[11.5px] leading-snug text-muted" data-id="element-2392" data-mp="1hfw63bkpelqz">{t('eng.ch.optional')}</p>
                {challengeDurations.map(duration => {
            const group = challenges.filter(item => item.duration === duration);
            if (group.length === 0) return null;
            return <section key={duration} aria-labelledby={`ch-${duration}`} className="mb-3 last:mb-0" data-id="element-2393" data-mp="2f2bnkdd4rjej">
                      <h2 id={`ch-${duration}`} className="mb-1 px-1 text-[12px] font-bold text-deep" data-id="element-2394" data-mp="20dffigb2q6dx">
                        {t(`eng.ch.section.${duration}`)}
                      </h2>
                      <ul className="space-y-2" data-id="element-2395" data-mp="2t1ulyeg53gdn">
                        {group.map(challenge => <li key={challenge.id} data-id="element-2396" data-mp="2xuxfimkx2ptu">
                            <ChallengeCard challenge={challenge} data-id="element-2397" data-mp="37p2ybil6prr7" />
                          </li>)}
                      </ul>
                    </section>;
          })}
              </div>)}

          {tab === 'members' && <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2398" data-mp="2xszvsbmf7r3q">
              {members.map(person => <PersonRow key={person.id} person={person} data-id="element-2399" data-mp="2oqz55xlp03b1" />)}
            </ul>}
        </div>
      </div>

      <CreateFab data-id="element-2400" data-mp="2jyyylmb4ac5h" />
    </div>;
}